/**
 * Initializes an Alpine.js data object to manage the addition of products to the cart, including cross-sell options.
 * Key features include:
 * 1. Cart Integration: Adds selected product variants to the cart with optional cross-sell products.
 * 2. Error Handling: Manages responses from the cart addition process, providing user feedback on failure.
 * 3. Sticky Button Visibility: Utilizes an IntersectionObserver to toggle the visibility of a sticky add-to-cart button based on scroll position.
 * 4. Dynamic UI Adjustments: Adapts the cart drawer's behavior based on screen width and user interactions.
 */

const heroProductAdd = () => ({
  drawerSide: '',
  stickyButtonVisible: false,

  addToCart: async function () {
    const itemsToAdd = [{ id: this.finalSelectedVariant.id }];

    if (this.crossSellChecked && this.crossSellVariant.id) {
      itemsToAdd.push({ id: this.crossSellVariant.id });
    }

    try {
      this.isWorking = true;
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'post',
        body: JSON.stringify({ items: itemsToAdd }),
        headers: { 'content-type': 'application/json' },
      });

      if (!res.ok) {
        let description = '';
        try {
          const data = await res.json();
          description = data?.description;
        } catch (e) {
          //
        }
        throw new Error([`Failed to add to cart!`, description, `(status: ${res.status})`].filter(Boolean).join('\n'));
      }

      this.$dispatch('update-and-open-cart', {
        noOpenAfterUpdate: window.CURRENT_TEMPLATE === 'cart',
      });
    } catch (error) {
      alert(error.message);
      this.isWorking = false;
      throw error;
    }
  },

  init() {
    this.setupStickyButton();
  },

  destroy() {
    this.intersectionObserver?.disconnect();
  },

  async handleButtonClick() {
    const { finalSelectedVariant } = this;
    if (finalSelectedVariant.availableForSale) {
      await this.addToCart();
      this.isWorking = false;
    } else {
      this.$dispatch('open-quick-add-drawer', {
        product: this.currentProduct,
        selectedOptions: this.selectedOptions,
      });
    }
  },

  handleDrawerOpen(e) {
    const matcher = window.matchMedia(`(min-width: 1024px)`);
    this.drawerSide = matcher.matches ? 'right' : 'bottom';

    e.target.closest('button').parentElement.querySelector('dialog').showModal();
  },

  setupStickyButton() {
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.stickyButtonVisible = !entry.isIntersecting;
    }, {});

    this.intersectionObserver.observe(this.$refs.button);
  },
});

document.addEventListener('alpine:init', () => {
  Alpine.data('heroProductAdd', heroProductAdd);
});
